import { useEffect, useState } from 'react';
import Modal from './Modal';
import { revenueApi } from '../utils/api';
import { formatIsoMonth, addMonths, currentMonth } from '../utils/revenueFormat';
import Icon from './Icon';
import ExpandCaret from './ExpandCaret';

// כיסוי הכנסות — the calendar half of the revenue section: month navigation, a
// ימי פעילות row for marking closures, ONE coverage strip for the restaurant,
// and a day calendar underneath it in the same colours.
//
// The strip used to be one bar PER EXPENSE LINE, each with its own N/M count.
// That only made sense while revenue was typed per category; a day either has
// the restaurant's Z-report or it does not. One strip, one truth.
//
// Tapping a day fills the entry form with that single day. Ranges are chosen in
// the form's PeriodPicker, so the old two-tap start/end dance is gone.
//
// Colours (one vocabulary across bar and calendar):
//   green  — revenue entered
//   orange — the day carries spend but no revenue (the gap the banner nags about)
//   hatched — סגור: the restaurant was shut. NOT green (green means "you typed
//             something") and NOT orange (orange means "do something"): a closed
//             day needs no revenue and is never counted as missing.
//   grey outline — future (outside the coverage denominator)
//   plain  — past open day, no entry, no spend

const DAY_COLORS = {
  covered:          { background: '#3f6b5c', border: '1px solid #3f6b5c' },
  spend_no_revenue: { background: '#f6ad55', border: '1px solid #dd8b2e' },
  closed:           {
    background: 'repeating-linear-gradient(45deg,#eef2f0,#eef2f0 3px,#dbe3e0 3px,#dbe3e0 6px)',
    border: '1px solid #cbd5e1',
  },
  future:           { background: '#fff', border: '1px dashed #cbd5e1' },
  none:             { background: '#eef2f0', border: '1px solid #e2e8f0' },
};

const stateLabel = {
  covered: 'הוזן', spend_no_revenue: 'יש הוצאות בלי הכנסות', closed: 'סגור',
  future: 'עתידי', none: 'ללא נתונים',
};

const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/**
 * "We're closed every Monday" → real rows, one per Monday in the range.
 * Materialised deliberately (see database.js): a stored RULE would silently
 * rewrite last year's ratios the day someone changes their closing day.
 */
function WeeklyClosureDialog({ month, onClose, onSaved }) {
  const [days, setDays] = useState([]);
  const [from, setFrom] = useState(`${month}-01`);
  const [to, setTo] = useState(`${month.slice(0, 4)}-12-31`);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (days.length === 0) { setError('יש לבחור לפחות יום אחד בשבוע'); return; }
    setSaving(true); setError('');
    try {
      await revenueApi.addWeeklyClosedDays({ weekdays: days, date_from: from, date_to: to, note: 'סגירה קבועה' });
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'שגיאה בשמירת ימי הסגירה');
    } finally { setSaving(false); }
  };

  return (
    <Modal
      onClose={onClose}
      ariaLabel="סגירה קבועה"
      size={460}
    >
      <h2 className="modal-title" style={{ marginBottom: 4 }}>סגירה קבועה</h2>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--ink-muted)' }}>
        נסמן את כל הימים האלה בטווח כסגורים. אפשר לשנות כל יום בנפרד אחר כך.
      </p>
      <form onSubmit={submit}>
        {error && (
          <div className="badge-red" style={{ display: 'block', marginBottom: 10, padding: '8px 12px', borderRadius: 8 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {WEEKDAYS.map((name, i) => {
            const on = days.includes(i);
            return (
              <button
                key={i} type="button"
                aria-pressed={on}
                onClick={() => setDays(d => (on ? d.filter(x => x !== i) : [...d, i]))}
                style={{
                  padding: '7px 12px', borderRadius: 8, fontSize: 12.5, cursor: 'pointer',
                  border: on ? '1px solid #3f6b5c' : '1px solid #e2e8f0',
                  background: on ? '#e6efe9' : '#fff',
                  color: on ? '#2c5446' : '#475569', fontWeight: on ? 600 : 400,
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5, color: '#475569' }}>
            מתאריך
            <input className="form-control" type="date" value={from} aria-label="מתאריך סגירה קבועה"
              onChange={e => setFrom(e.target.value)} style={{ padding: '6px 10px' }} />
          </label>
          <label style={{ fontSize: 12.5, color: '#475569' }}>
            עד תאריך
            <input className="form-control" type="date" value={to} aria-label="עד תאריך סגירה קבועה"
              onChange={e => setTo(e.target.value)} style={{ padding: '6px 10px' }} />
          </label>
        </div>
        <div className="modal-actions" style={{ marginTop: 14 }}>
          <button className="btn btn-secondary" type="button" onClick={onClose}>ביטול</button>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'שומר…' : 'סימון הימים כסגורים'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function RevenueCoverageView({ onPickRange, canEdit = true, refreshToken = 0, openDaysRef }) {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // The day calendar under the strip. Collapsed by default: the strip alone
  // answers "which days am I missing", and the calendar is only needed to act.
  const [openStrip, setOpenStrip] = useState(false);
  // ימי פעילות — the restaurant-level row. `closed` is the month's closed set;
  // `openRow` is whether its calendar is expanded.
  const [closed, setClosed] = useState([]);
  const [openRow, setOpenRow] = useState(false);
  const [savingClosed, setSavingClosed] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    let stale = false;
    setError('');
    revenueApi.coverage(month)
      .then(r => { if (!stale) setData(r.data); })
      .catch(e => { if (!stale) { setData(null); setError(e?.response?.data?.error || 'שגיאה בטעינת הכיסוי'); } });
    return () => { stale = true; };
  }, [month, refreshToken, bump]);

  useEffect(() => {
    let stale = false;
    revenueApi.closedDays(month)
      .then(r => { if (!stale) setClosed(r.data?.dates || []); })
      .catch(() => { if (!stale) setClosed([]); });
    return () => { stale = true; };
  }, [month, refreshToken, bump]);

  // One PUT replaces the month — no diffing, and a double tap is harmless.
  const toggleClosed = async (date) => {
    if (!canEdit || savingClosed) return;
    const next = closed.includes(date) ? closed.filter(d => d !== date) : [...closed, date].sort();
    setClosed(next);                       // optimistic; the reload below is truth
    setSavingClosed(true);
    try {
      await revenueApi.setClosedDays({ month, dates: next });
      setBump(b => b + 1);
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה בשמירת ימי הסגירה');
      setBump(b => b + 1);
    } finally { setSavingClosed(false); }
  };

  // One tap, one day. The two-tap start/end range picking is gone: the entry
  // form now carries a real PeriodPicker for ranges, which leaves this calendar
  // with the single job the owner asked it to keep — fixing one day.
  const pickDay = (day) => {
    if (!canEdit) return;
    if (day.state === 'future') return;
    onPickRange?.({ dateFrom: day.date, dateTo: day.date });
  };

  if (error) return <p style={{ color: '#b45309', fontSize: 13 }}>{error}</p>;
  if (!data) return <div className="loading-container"><div className="spinner" /></div>;

  // The denominator is OPEN days, not calendar days: a closed day can never
  // be "covered", so counting it made the ratio permanently unreachable.
  const openDays = data.open_days;

  return (
    <div dir="rtl">
      {/* Month navigation, matching PeriodPicker's convention exactly: the
          chevron glyph points LEFT (= forward in this RTL app), so PREVIOUS
          is the mirrored one and NEXT is the plain one. These two were
          swapped, which pointed both arrows the wrong way. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px' }}
          aria-label="חודש קודם" onClick={() => setMonth(m => addMonths(m, -1))}>
          <Icon name="chevron-inline" size={13} flip />
        </button>
        <strong style={{ fontSize: 14 }}>{formatIsoMonth(month)}</strong>
        <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px' }}
          aria-label="חודש הבא" onClick={() => setMonth(m => addMonths(m, 1))}>
          <Icon name="chevron-inline" size={13} />
        </button>
      </div>

      {/* ימי פעילות — the restaurant-level row, ABOVE the per-line bars because
          every one of them depends on it: a closed day is a property of the
          RESTAURANT, not of one expense line, and marking it inside a per-line
          calendar would teach the wrong model. This is also where closed days
          are discovered — the ₪0 idiom is gone. */}
      <div ref={openDaysRef} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #eef1f5' }}>
        <button
          type="button"
          onClick={() => setOpenRow(o => !o)}
          aria-expanded={openRow}
          aria-label="ימי פעילות וסגירה"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            background: 'none', border: 0, padding: '4px 0', cursor: 'pointer', textAlign: 'start',
          }}
        >
          <span style={{
            display: 'inline-block', width: 10, height: 10, borderRadius: 2,
            ...DAY_COLORS.closed, flexShrink: 0,
          }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>ימי פעילות</span>
          <span style={{ fontSize: 12, color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>
            {data.closed_days > 0
              ? `${data.open_days} ימי פעילות · ${data.closed_days} ימי סגירה`
              : `${data.open_days} ימי פעילות`}
          </span>
          <ExpandCaret open={openRow} size={13} />
        </button>

        {openRow && (
          <div style={{ margin: '6px 0 0', padding: '10px 12px', background: '#fafaf7', borderRadius: 8 }}>
            {canEdit && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <p style={{ margin: 0, flex: '1 1 auto', fontSize: 12, color: 'var(--ink-muted)' }}>
                  הקישו על יום כדי לסמן שהמסעדה הייתה סגורה. יום סגור לא ייחשב חסר הכנסות.
                </p>
                <button type="button" className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => setWeeklyOpen(true)}>
                  סגורים בקביעות ביום מסוים?
                </button>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(28px, 1fr))', gap: 3 }}>
              {data.days.map(day => {
                const isClosed = closed.includes(day.date);
                const isFuture = day.state === 'future';
                const dayNum = Number(day.date.slice(8));
                return (
                  <button
                    key={day.date} type="button"
                    onClick={() => toggleClosed(day.date)}
                    disabled={!canEdit}
                    aria-pressed={isClosed}
                    aria-label={`${day.date} — ${isClosed ? 'סגור' : 'יום פעילות'}`}
                    title={isClosed ? 'סגור — הקישו כדי לבטל' : 'יום פעילות — הקישו כדי לסמן כסגור'}
                    style={{
                      ...(isClosed ? DAY_COLORS.closed : { background: '#fff', border: '1px solid #e2e8f0' }),
                      color: isFuture ? '#94a3b8' : '#475569',
                      borderRadius: 4, fontSize: 11.5, padding: '5px 0',
                      cursor: canEdit ? 'pointer' : 'default',
                    }}
                  >
                    {dayNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {weeklyOpen && (
        <WeeklyClosureDialog
          month={month}
          onClose={() => setWeeklyOpen(false)}
          onSaved={() => { setWeeklyOpen(false); setBump(b => b + 1); }}
        />
      )}

      {/* ONE strip for the restaurant. There used to be one per קטגוריה,
          each with its own N/M count — which only made sense while revenue was
          entered per category. A day either has the restaurant's Z-report or it
          does not, and that is a single fact. */}
      <div style={{ marginBottom: openStrip ? 14 : 6 }}>
        <button
          type="button"
          onClick={() => setOpenStrip(o => !o)}
          aria-expanded={openStrip}
          aria-label="כיסוי ימי פדיון"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            background: 'none', border: 0, padding: '4px 0', cursor: 'pointer', textAlign: 'start',
          }}
        >
          <span style={{ minWidth: 72, fontSize: 13, fontWeight: 600 }}>כיסוי ימים</span>
          <span style={{ display: 'flex', gap: 1.5, flex: 1 }}>
            {data.days.map(day => (
              <span key={day.date} title={`${day.date} · ${stateLabel[day.state]}`} style={{
                ...DAY_COLORS[day.state], flex: 1, height: 10, borderRadius: 2, minWidth: 3,
              }} />
            ))}
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>
            {data.covered_days}/{openDays} ימי פעילות
          </span>
          <ExpandCaret open={openStrip} size={13} />
        </button>

        {openStrip && (
          <div style={{ margin: '6px 0 0 0', padding: '10px 12px', background: '#fafaf7', borderRadius: 8 }}>
            {canEdit && (
              <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--ink-muted)' }}>
                הקישו על יום כדי להזין פדיון ליום בודד. לטווח שלם — בחרו תקופה בטופס למעלה.
              </p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(28px, 1fr))', gap: 3 }}>
              {data.days.map(day => {
                const dayNum = Number(day.date.slice(8));
                return (
                  <button
                    key={day.date} type="button"
                    onClick={() => pickDay(day)}
                    aria-label={`${day.date} — ${stateLabel[day.state]}`}
                    title={stateLabel[day.state]}
                    style={{
                      ...DAY_COLORS[day.state],
                      color: day.state === 'covered' ? '#fff' : '#475569',
                      borderRadius: 4, fontSize: 11.5, padding: '5px 0',
                      cursor: canEdit && day.state !== 'future' ? 'pointer' : 'default',
                    }}
                  >
                    {dayNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* One legend for the whole card, always visible — the colors have to
          read before anyone opens the calendar, not after. `title` on the
          meaningful swatches mirrors the day cells' own tooltip (native
          `title`, so it only fires on mouse hover — desktop only, by
          construction, with no touch/media handling needed). */}
      {(
        <div style={{
          display: 'flex', gap: 14, marginTop: 10, paddingTop: 10,
          borderTop: '1px solid #eef1f5', fontSize: 11.5, color: 'var(--ink-muted)', flexWrap: 'wrap',
        }}>
          <span title="יום פעילות שהוזן לו פדיון">
            <span style={{ ...DAY_COLORS.covered, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />
            הוזן
          </span>
          <span title="יום עם הוצאות בפועל שעדיין לא הוזנה לו הכנסה">
            <span style={{ ...DAY_COLORS.spend_no_revenue, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />
            יש הוצאות בלי הכנסות
          </span>
          <span title="המסעדה הייתה סגורה — לא נספר כחסר. הוצאות של יום סגור עדיין נכללות באחוז, כי הסחורה נמכרת בימי הפעילות">
            <span style={{ ...DAY_COLORS.closed, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />
            סגור
          </span>
          <span>
            <span style={{ ...DAY_COLORS.future, display: 'inline-block', width: 10, height: 10, borderRadius: 2, verticalAlign: 'middle', marginInlineEnd: 4 }} />
            עתידי
          </span>
        </div>
      )}
    </div>
  );
}
